import { DragEvent, FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import AddIcon from '@mui/icons-material/Add';
import VideocamIcon from '@mui/icons-material/Videocam';
import { Box, Stack, Tooltip, Typography } from '@mui/material';

import { FilePointerWithMeta, PostgresFilePointer, S3FilePointer } from '@/api/data-contracts';
import ImagesModal from '@/features/media/ui/ImagesModal';
import {
  DragContainer,
  UploadButtonContainer,
} from '@/features/media/ui/ServiceTaskImagesPreview/ServiceTaskImagesPreview.styles';
import {
  ArtworkThumbnail,
  MoreArtworkButton,
  NonImageFileThumbnail,
} from '@/features/media/ui/TableCellFilesPreview/TableCellFilesPreview.styles';
import useUploadedFilesStore from '@/shared/api/uploadedFiles';
import { filesTooLargeMessage } from '@/shared/lib/files';
import { useSnackbar } from '@/shared/lib/hooks/useAppSnackbar';
import { ImageResource, useResourceImage } from '@/shared/lib/hooks/useResourceImage';

const MAX_FILE_SIZE = 30 * 1024 * 1024; // 30MB

type MonitorFile = PostgresFilePointer | S3FilePointer | FilePointerWithMeta;
type MonitorCategory = 'Decoration' | 'Neutralization';

const extractFilePointer = (
  file: PostgresFilePointer | S3FilePointer | FilePointerWithMeta,
): PostgresFilePointer | S3FilePointer => {
  if ('file' in file) {
    return file.file;
  }
  return file;
};

interface ThumbProps {
  file: MonitorFile;
  buildResource: (documentId: string) => ImageResource;
  onOpen: (objectId?: string | null) => void;
}

const VIDEO_EXTENSIONS = ['.mp4', '.webm', '.ogg', '.mov', '.avi'];

const MonitorThumbnailImpl: FC<ThumbProps> = ({ file, buildResource, onOpen }) => {
  const objectId = extractFilePointer(file).objectId ?? '';
  const name = extractFilePointer(file).name ?? 'image';
  const [previewRequested, setPreviewRequested] = useState(false);
  const isVideo = VIDEO_EXTENSIONS.some((ext) => name.toLowerCase().endsWith(ext));

  const resource = useMemo<ImageResource | null>(
    () => (!isVideo && objectId ? buildResource(objectId) : null),
    [buildResource, objectId, isVideo],
  );

  const { url: thumbUrl } = useResourceImage(resource, { size: { tier: 'thumb' } });
  const { url: previewUrl } = useResourceImage(resource, {
    size: { tier: 'preview' },
    enabled: previewRequested,
  });

  const handleClick = useCallback(() => onOpen(objectId || null), [onOpen, objectId]);
  const tooltipSrc = previewUrl ?? thumbUrl;

  if (isVideo) {
    return (
      <Tooltip placement="top" title={name}>
        <NonImageFileThumbnail onClick={handleClick}>
          <VideocamIcon sx={{ color: '#c2c3ce', fontSize: '1.2rem' }} />
        </NonImageFileThumbnail>
      </Tooltip>
    );
  }

  return (
    <Tooltip
      placement="top"
      enterDelay={150}
      onOpen={() => setPreviewRequested(true)}
      title={
        tooltipSrc ? (
          <Box
            component="img"
            src={tooltipSrc}
            alt={name}
            sx={{ width: '200px', height: 'auto' }}
          />
        ) : null
      }
    >
      <ArtworkThumbnail src={thumbUrl ?? undefined} alt={name} onClick={handleClick} />
    </Tooltip>
  );
};

const MonitorThumbnail = memo(MonitorThumbnailImpl);

interface Props {
  resourceId: string;
  category: MonitorCategory;
  beforeDecorationFiles?: MonitorFile[];
  afterDecorationFiles?: MonitorFile[];
  decorationFiles?: MonitorFile[];
  beforeNeutralizationFiles?: MonitorFile[];
  afterNeutralizationFiles?: MonitorFile[];
  neutralizationFiles?: MonitorFile[];
  buildResource: (documentId: string) => ImageResource;
  onUpload?: (files: File[], formKey: string) => Promise<any>;
  onDelete?: (objectId: string) => Promise<any>;
  hasUploadOption?: boolean;
  hasDeleteOption?: boolean;
}

const CampaignMonitorPhotosTrigger: FC<Props> = ({
  resourceId,
  category,
  beforeDecorationFiles = [],
  afterDecorationFiles = [],
  decorationFiles = [],
  beforeNeutralizationFiles = [],
  afterNeutralizationFiles = [],
  neutralizationFiles = [],
  buildResource,
  onUpload,
  onDelete,
  hasUploadOption = true,
  hasDeleteOption = true,
}) => {
  const { t } = useTranslation();
  const [modalOpen, setModalOpen] = useState(false);
  const [forceOpen, setForceOpen] = useState(false);
  const [forceClose, setForceClose] = useState(false);
  const [initialSelectedObjectId, setInitialSelectedObjectId] = useState<string | null>(null);
  const [autoExpandInitialSelection, setAutoExpandInitialSelection] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const { enqueueSnackbar } = useSnackbar();

  const pendingOpen = useUploadedFilesStore((s) => s.pendingOpen);
  const clearPendingOpen = useUploadedFilesStore((s) => s.clearPendingOpen);
  const activeContextKey = useUploadedFilesStore((s) => s.activeContextKey);
  const registerOpenContext = useUploadedFilesStore((s) => s.registerOpenContext);
  const unregisterOpenContext = useUploadedFilesStore((s) => s.unregisterOpenContext);

  const myContextKey = `${resourceId}-${category}`;

  useEffect(() => {
    if (!pendingOpen || pendingOpen.contextKey !== myContextKey) return;
    setForceClose(false);
    setForceOpen(true);
    if (pendingOpen.attachmentId) {
      setInitialSelectedObjectId(pendingOpen.attachmentId);
      setAutoExpandInitialSelection(true);
    }
    registerOpenContext(myContextKey);
    clearPendingOpen();
  }, [pendingOpen, myContextKey, clearPendingOpen, registerOpenContext]);

  useEffect(() => {
    if (forceOpen) setModalOpen(true);
  }, [forceOpen]);

  useEffect(() => {
    if (forceOpen) setForceOpen(false);
  }, [forceOpen]);

  useEffect(() => {
    if (!activeContextKey || activeContextKey === myContextKey) return;
    setForceClose(true);
    unregisterOpenContext(myContextKey);
  }, [activeContextKey, myContextKey, unregisterOpenContext]);

  useEffect(() => {
    if (forceClose) setForceClose(false);
  }, [forceClose]);

  useEffect(() => {
    if (forceClose) setModalOpen(false);
  }, [forceClose]);

  const formKey = useMemo(
    () =>
      category === 'Decoration'
        ? ('afterDecorationFiles' as const)
        : ('afterNeutralizationFiles' as const),
    [category],
  );

  const handleDragOver = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    if (hasUploadOption && onUpload && !isDragging) setIsDragging(true);
  };

  const handleDragLeave = (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDrop = async (e: DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    if (!hasUploadOption || !onUpload) return;

    const dtFiles = e.dataTransfer.files;
    if (!dtFiles || dtFiles.length === 0) return;

    const all = Array.from(dtFiles);
    const invalid = all.filter((f) => f.size > MAX_FILE_SIZE);
    const valid = all.filter((f) => f.size <= MAX_FILE_SIZE);

    if (invalid.length) {
      enqueueSnackbar(
        filesTooLargeMessage(
          t,
          invalid.map((f) => f.name),
          MAX_FILE_SIZE,
        ),
        { variant: 'warning' },
      );
    }

    if (!valid.length) return;
    await onUpload(valid, formKey);
  };

  const activeFiles = useMemo(
    () => (category === 'Decoration' ? decorationFiles : neutralizationFiles),
    [category, decorationFiles, neutralizationFiles],
  );

  const openModal = useCallback(
    (objectId?: string | null) => {
      setInitialSelectedObjectId(objectId ?? null);
      setModalOpen(true);
      registerOpenContext(myContextKey);
    },
    [myContextKey, registerOpenContext],
  );

  const buildModalResource = useCallback(
    (objectId: string): ImageResource | null => buildResource(objectId),
    [buildResource],
  );

  return (
    <>
      <DragContainer
        direction="row"
        alignItems="center"
        isDragging={isDragging}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        {activeFiles.length > 0 ? (
          <Stack direction="row" alignItems="center" ml="0.125rem">
            {activeFiles.slice(0, 5).map((file) => {
              const fp = extractFilePointer(file);
              return (
                <MonitorThumbnail
                  key={fp.objectId}
                  file={file}
                  buildResource={buildResource}
                  onOpen={openModal}
                />
              );
            })}

            {activeFiles.length > 5 && (
              <MoreArtworkButton onClick={() => openModal(null)}>
                +{activeFiles.length - 5}
              </MoreArtworkButton>
            )}
          </Stack>
        ) : (
          <Stack direction="row" alignItems="center" gap={1}>
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ whiteSpace: 'nowrap', fontWeight: 400 }}
            >
              {isDragging ? 'Drop here' : t('not_uploaded')}
            </Typography>
            {hasUploadOption && (
              <UploadButtonContainer onClick={() => openModal(null)}>
                <AddIcon fontSize="small" />
              </UploadButtonContainer>
            )}
          </Stack>
        )}
      </DragContainer>

      <ImagesModal
        open={modalOpen}
        onClose={() => {
          setModalOpen(false);
          setInitialSelectedObjectId(null);
          setAutoExpandInitialSelection(false);
          unregisterOpenContext(myContextKey);
        }}
        taskId={resourceId}
        beforeDecorationFiles={beforeDecorationFiles}
        afterDecorationFiles={afterDecorationFiles}
        decorationFiles={decorationFiles}
        beforeNeutralizationFiles={beforeNeutralizationFiles}
        afterNeutralizationFiles={afterNeutralizationFiles}
        neutralizationFiles={neutralizationFiles}
        initialTab={category}
        initialSelectedObjectId={initialSelectedObjectId}
        autoExpandInitialSelection={autoExpandInitialSelection}
        splitMonitorMode={true}
        mode="monitor"
        buildResource={buildModalResource}
        onUpload={onUpload}
        onDelete={onDelete}
        hasUploadOption={hasUploadOption}
        hasDeleteOption={hasDeleteOption}
        requiredPhotoDeleteGuard={{
          active: true,
          message: t('monitor_photo_delete_blocked_required'),
        }}
      />
    </>
  );
};

export default CampaignMonitorPhotosTrigger;
